import React, { useCallback, useEffect, useState } from 'react';
import {
  Server, RefreshCw, CheckCircle, AlertTriangle, XCircle, Filter,
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { formatCompactNumber, formatDateTime } from '../utils/format';
import './AdminPage.css';

const STATUS_ICON: Record<string, React.ReactNode> = {
  healthy: <CheckCircle size={14} color="#16a34a" />,
  degraded: <AlertTriangle size={14} color="#f59e0b" />,
  down: <XCircle size={14} color="#dc2626" />,
  unconfigured: <Filter size={14} color="#94a3b8" />,
  unknown: <AlertTriangle size={14} color="#94a3b8" />,
};

const Providers: React.FC = () => {
  const [providers, setProviders] = useState<any[]>([]);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    const [p, w] = await Promise.allSettled([
      adminService.getProvidersHealth(),
      adminService.getPendingWithdrawals(),
    ]);
    if (p.status === 'fulfilled') {
      setProviders(p.value?.providers || []);
      setCheckedAt(p.value?.checked_at || null);
    }
    if (w.status === 'fulfilled') {
      const list = Array.isArray(w.value) ? w.value : w.value?.withdrawals || w.value?.data || [];
      setPendingWithdrawals(list);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const healthy = providers.filter((p) => p.status === 'healthy').length;
  const down = providers.filter((p) => p.status === 'down').length;
  const unconfigured = providers.filter((p) => p.status === 'unconfigured').length;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Server size={20} style={{ marginRight: 8 }} />Payment & Infra Providers</h1>
          <p className="page-subtitle">
            Live health snapshot for Paystack, Wise, Monnify, SendGrid, Redis, R2 and the database
            {checkedAt && <> · last checked {formatDateTime(checkedAt)}</>}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Total checks</span>
          <span className="kpi-value">{providers.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Healthy</span>
          <span className="kpi-value" style={{ color: '#16a34a' }}>{healthy}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Down</span>
          <span className="kpi-value" style={{ color: '#dc2626' }}>{down}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Unconfigured</span>
          <span className="kpi-value" style={{ color: '#94a3b8' }}>{unconfigured}</span>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3>Provider checks</h3>
          <span className="kpi-detail">Auto-refreshes every 30s</span>
        </div>
        <div className="panel-body tight">
          {loading ? <div className="skeleton" style={{ height: 180, margin: 16 }} /> : providers.length === 0 ? (
            <div className="empty">No provider data</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider</th><th>Status</th><th>Latency</th>
                  <th>Balance</th><th>Last error</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.provider}>
                    <td style={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.provider}</td>
                    <td>
                      <span className={`pill ${p.status}`}>
                        {STATUS_ICON[p.status] || STATUS_ICON.unknown}
                        {p.status}
                      </span>
                    </td>
                    <td>{p.latency_ms != null ? `${p.latency_ms}ms` : '—'}</td>
                    <td>
                      {p.balance != null
                        ? `${formatCompactNumber(p.balance)} ${p.balance_currency || ''}`
                        : '—'}
                    </td>
                    <td style={{ color: '#dc2626', fontSize: 12 }}>{p.last_error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3>Pending withdrawals</h3>
          <span className="kpi-detail">{pendingWithdrawals.length} pending</span>
        </div>
        <div className="panel-body tight">
          {pendingWithdrawals.length === 0 ? (
            <div className="empty">No pending withdrawals</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ref</th><th>User</th><th>Amount</th>
                    <th>Bank</th><th>Method</th><th>Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingWithdrawals.slice(0, 50).map((w: any) => (
                    <tr key={w.id || w.reference}>
                      <td>{w.reference || w.id}</td>
                      <td>#{w.user_id}</td>
                      <td>{formatCompactNumber(w.amount)} {w.currency_code}</td>
                      <td>{w.bank_name || w.bank_code || '—'}</td>
                      <td>{w.method || '—'}</td>
                      <td>{formatDateTime(w.created_at || w.requested_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Providers;
